import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Button, Spinner } from 'react-bootstrap';

import BaseModal from '@/components/BaseModal.js';

import {
  clampCropToBounds,
  createCenteredCrop,
  cropRotatedJianyingImageToBlob,
  resizeCropFromBottomRight,
} from '@/features/automation/jianying/utils/jianyingImageCrop.js';

const RATIOS = [
  { key: '16:9', label: '16:9', aspect: 16 / 9 },
  { key: '4:3', label: '4:3', aspect: 4 / 3 },
  { key: '1:1', label: '1:1', aspect: 1 },
  { key: 'free', label: '自由', aspect: null },
];

const JianyingManualCropModal = ({
  image,
  onCancel,
  onSave,
  show,
}) => {
  const [ratioKey, setRatioKey] = useState('16:9');
  const [quarterRotation, setQuarterRotation] = useState(0);
  const [fineRotation, setFineRotation] = useState(0);
  const [crop, setCrop] = useState({ x: 5, y: 5, width: 90, height: 90 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const aspect = RATIOS.find((ratio) => ratio.key === ratioKey)?.aspect || null;
  const originalUrl = image?.originalUrl || image?.url || '';
  const rotation = quarterRotation + fineRotation;
  const displayedRotation = Number((
    ((rotation + 180) % 360 + 360) % 360 - 180
  ).toFixed(1));

  useEffect(() => {
    if (!show) return undefined;
    setRatioKey('16:9');
    setQuarterRotation(0);
    setFineRotation(0);
    setCrop({ x: 5, y: 5, width: 90, height: 90 });
    setDisplaySize({ width: 0, height: 0 });
    setMessage('');
    return undefined;
  }, [originalUrl, show]);

  const resetCrop = useCallback((nextAspect = aspect) => {
    if (!displaySize.width || !displaySize.height) return;
    setCrop(createCenteredCrop({
      displayWidth: displaySize.width,
      displayHeight: displaySize.height,
      aspect: nextAspect,
    }));
  }, [aspect, displaySize.height, displaySize.width]);

  const handleImageLoad = (event) => {
    const nextSize = {
      width: event.currentTarget.clientWidth,
      height: event.currentTarget.clientHeight,
    };
    setDisplaySize(nextSize);
    setCrop(createCenteredCrop({
      displayWidth: nextSize.width,
      displayHeight: nextSize.height,
      aspect,
    }));
  };

  const chooseRatio = (ratio) => {
    setRatioKey(ratio.key);
    if (displaySize.width && displaySize.height) {
      setCrop(createCenteredCrop({
        displayWidth: displaySize.width,
        displayHeight: displaySize.height,
        aspect: ratio.aspect,
      }));
    }
  };

  const beginPointerAction = (type, event) => {
    if (busy || !displaySize.width || !displaySize.height) return;
    event.preventDefault();
    event.stopPropagation();
    const start = {
      x: event.clientX,
      y: event.clientY,
      crop,
    };
    const move = (pointerEvent) => {
      const deltaXPercent = (
        (pointerEvent.clientX - start.x) / displaySize.width * 100
      );
      const deltaYPercent = (
        (pointerEvent.clientY - start.y) / displaySize.height * 100
      );
      if (type === 'move') {
        setCrop(clampCropToBounds({
          ...start.crop,
          x: start.crop.x + deltaXPercent,
          y: start.crop.y + deltaYPercent,
        }));
      } else {
        setCrop(resizeCropFromBottomRight({
          crop: start.crop,
          deltaXPercent,
          deltaYPercent,
          displayWidth: displaySize.width,
          displayHeight: displaySize.height,
          aspect,
        }));
      }
    };
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end, { once: true });
  };

  const save = async () => {
    if (!originalUrl) return;
    setBusy(true);
    setMessage('');
    try {
      const blob = await cropRotatedJianyingImageToBlob(originalUrl, crop, rotation);
      onSave(blob);
    } catch (error) {
      setMessage(error.message || '生成裁剪图片失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <BaseModal
      show={show}
      onClose={onCancel}
      closable={!busy}
      title="裁剪"
      className="jianying-manual-crop-modal"
      footer={(
        <>
          <Button variant="outline-secondary" disabled={busy} onClick={() => resetCrop()}>
            重置裁剪框
          </Button>
          <Button variant="secondary" disabled={busy} onClick={onCancel}>
            取消
          </Button>
          <Button disabled={busy || !originalUrl} onClick={save}>
            使用此裁剪
          </Button>
        </>
      )}
    >
      <div className="jianying-manual-crop-modal__toolbar">
        <div>
          <span>比例</span>
          {RATIOS.map((ratio) => (
            <button
              key={ratio.key}
              type="button"
              className={ratio.key === ratioKey ? 'is-active' : ''}
              disabled={busy}
              onClick={() => chooseRatio(ratio)}
            >
              {ratio.label}
            </button>
          ))}
        </div>
        <div className="jianying-manual-crop-modal__rotation-controls">
          <span>旋转</span>
          <button
            type="button"
            disabled={busy}
            onClick={() => setQuarterRotation((current) => (current + 270) % 360)}
            aria-label="向左旋转 90 度"
          >
            <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setQuarterRotation((current) => (current + 90) % 360)}
            aria-label="向右旋转 90 度"
          >
            <i className="bi bi-arrow-clockwise" aria-hidden="true" />
          </button>
          <input
            type="range"
            min="-45"
            max="45"
            step="0.1"
            value={fineRotation}
            disabled={busy}
            onChange={(event) => setFineRotation(Number(event.target.value))}
            aria-label="自由旋转角度"
          />
          <small>{displayedRotation}°</small>
        </div>
      </div>

      <div className="jianying-manual-crop-modal__workspace">
        {originalUrl ? (
          <div className="jianying-manual-crop-modal__stage">
            <img
              key={originalUrl}
              src={originalUrl}
              alt={image?.name || '待裁剪图片'}
              onLoad={handleImageLoad}
              style={{ transform: `rotate(${rotation}deg)` }}
            />
            {displaySize.width ? (
              <div
                className="jianying-manual-crop-modal__frame"
                style={{
                  left: `${crop.x}%`,
                  top: `${crop.y}%`,
                  width: `${crop.width}%`,
                  height: `${crop.height}%`,
                }}
                onPointerDown={(event) => beginPointerAction('move', event)}
              >
                <span className="is-third is-vertical first" />
                <span className="is-third is-vertical second" />
                <span className="is-third is-horizontal first" />
                <span className="is-third is-horizontal second" />
                <button
                  type="button"
                  className="jianying-manual-crop-modal__resize"
                  onPointerDown={(event) => beginPointerAction('resize', event)}
                  aria-label="拖动修改裁剪框大小"
                />
              </div>
            ) : null}
          </div>
        ) : null}
        {busy ? (
          <div className="jianying-manual-crop-modal__busy">
            <Spinner size="sm" /> 正在处理图片
          </div>
        ) : null}
      </div>
      {message ? <p className="jianying-image-field-error">{message}</p> : null}
    </BaseModal>
  );
};

export default JianyingManualCropModal;
